import { Calendar, AlertCircle, Sparkles, ShieldCheck, Clock } from 'lucide-react';
import {
  getInstallmentStatusSpec,
  type GridSlot,
} from '../../../domain/calculators/installments';
import { formatDate } from '../../../domain/formatters/date';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { getSlotMonthLabel, getSlotYear } from './installmentUtils';
import './installments.css';

export interface LiveMonthHUDProps {
  slot: GridSlot | null;
  onSelect?: (slot: GridSlot) => void;
}

export function LiveMonthHUD({ slot, onSelect }: LiveMonthHUDProps) {
  const { t, locale } = useTranslation();

  if (!slot) {
    return (
      <div className="inst-hud-bar inst-hud-bar--idle" aria-live="polite">
        <div className="inst-hud-idle-content">
          <Sparkles size={16} className="text-amber-500 animate-pulse" />
          <span>{t('installments.hudIdle')}</span>
        </div>
      </div>
    );
  }

  const monthName = getSlotMonthLabel(slot);
  const year = getSlotYear(slot);
  const statusSpec = getInstallmentStatusSpec(slot.row?.status, slot.row?.dueDate);
  const isCleared = slot.phase === 'cleared';
  const isActionable = statusSpec.isPayable;
  const dueDateStr = slot.row?.dueDate
    ? formatDate(slot.row.dueDate, locale)
    : monthName
    ? `${monthName} ${year ?? ''}`
    : '—';
  const hasAmount = Boolean(slot.row && Number.isFinite(slot.row.amount));
  const amountStr = hasAmount ? `৳ ${(slot.row!.amount).toLocaleString('en-IN')}` : '—';

  return (
    <div className={`inst-hud-bar inst-hud-bar--${slot.phase}`} aria-live="polite">
      <div className="inst-hud-left">
        <div className="inst-hud-badge-pill">
          <span className="inst-hud-month-num">M{String(slot.installmentNumber).padStart(3, '0')}</span>
          {monthName && (
            <span className="inst-hud-cal-tag">
              {monthName} {year ?? ''}
            </span>
          )}
        </div>

        <div className="inst-hud-meta">
          <div className="inst-hud-title-row">
            <strong className="inst-hud-phase-label">
              {t(statusSpec.labelKey).toUpperCase()}
            </strong>
            <span className="inst-hud-dot">•</span>
            <span className="inst-hud-amount">{amountStr}</span>
            <span className="inst-hud-dot">•</span>
            <span className="inst-hud-date">
              <Calendar size={12} /> {dueDateStr}
            </span>
          </div>

          {slot.phase === 'advance' && monthName && (
            <div className="inst-hud-sub-row">
              <span className="inst-hud-milestone-distance">
                <Clock size={11} /> {t('installments.advanceHint', { month: monthName })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="inst-hud-right">
        {isCleared && (
          <span className="inst-hud-equity-pill">
            <ShieldCheck size={13} color="#059669" />
            {t('installments.settledToLedger')}
          </span>
        )}

        {isActionable && (
          <span className="inst-hud-urgent-pill">
            <AlertCircle size={13} color="#dc2626" />
            {t('installments.activeDue')}
          </span>
        )}

        <button
          type="button"
          className="inst-hud-inspect-btn"
          onClick={() => onSelect?.(slot)}
        >
          {t('installments.inspectVoucher')}
        </button>
      </div>
    </div>
  );
}

export default LiveMonthHUD;
